import { ChangeDetectionStrategy, Component, DestroyRef, computed, effect, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { VerwendungCardComponent } from '../../../components/verwendung-card/verwendung-card.component';
import { TestCaseUsage } from '../../../models/test-case.model';
import { VerwendungReiter, VerwendungStatus } from '../../../models/verwendung.model';
import { TestCaseService } from '../../../services/test-case.service';
import { testRunExecutionsLink } from '../../../utils/test-run-routes';

const KEINE_VERWENDUNG: TestCaseUsage = { testPlans: [], testRuns: [] };

@Component({
  selector: 'app-test-case-usage',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslatePipe, VerwendungCardComponent],
  templateUrl: './test-case-usage.component.html',
})
export class TestCaseUsageComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly testCaseService = inject(TestCaseService);
  private readonly translate = inject(TranslateService);

  readonly projectId = input.required<string>();
  readonly testCaseId = input<string | null>(null);

  protected readonly status = signal<VerwendungStatus>('bereit');
  private readonly usage = signal<TestCaseUsage>(KEINE_VERWENDUNG);
  private readonly sprachwechsel = toSignal(this.translate.onLangChange, { initialValue: null });
  private laden: Subscription | null = null;

  protected readonly tabs = computed<VerwendungReiter[]>(() => {
    this.sprachwechsel();
    const projectId = this.projectId();
    const { testPlans, testRuns } = this.usage();
    return [
      {
        id: 'tp',
        label: this.translate.instant('testCases.form.usage.testPlans'),
        icon: 'shield',
        leerText: this.translate.instant('testCases.form.usage.testPlansEmpty'),
        eintraege: testPlans.map((plan) => ({
          id: plan.id,
          key: plan.key,
          titel: plan.name,
          ziel: { commands: ['/projects', projectId, 'test-plans', plan.id, 'edit'] },
        })),
      },
      {
        id: 'tl',
        label: this.translate.instant('testCases.form.usage.testRuns'),
        icon: 'bar_chart',
        leerText: this.translate.instant('testCases.form.usage.testRunsEmpty'),
        eintraege: testRuns.map((run) => ({
          id: run.id,
          key: run.key,
          titel: run.name,
          ziel: testRunExecutionsLink(projectId, run.id),
          konfiguration: run.configurations?.length ? run.configurations.join(', ') : undefined,
        })),
      },
    ];
  });

  constructor() {
    effect(() => this.lade(this.projectId(), this.testCaseId()));
  }

  protected erneutLaden(): void {
    this.lade(this.projectId(), this.testCaseId());
  }

  private lade(projectId: string, testCaseId: string | null): void {
    this.laden?.unsubscribe();
    this.usage.set(KEINE_VERWENDUNG);
    if (!testCaseId) {
      this.status.set('bereit');
      return;
    }
    this.status.set('laedt');
    this.laden = this.testCaseService
      .getUsage(projectId, testCaseId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (usage) => {
          this.usage.set(usage);
          this.status.set('bereit');
        },
        error: () => this.status.set('fehler'),
      });
  }
}
